import { createContext, useContext, useState, type ReactNode } from 'react';

/**
 * Header-slot plumbing for the unified app header.
 *
 * The shell renders ONE sticky `AppHeader`. Each page declares its
 * title / breadcrumb / contextual actions via the `<PageHeader>`
 * component, which portals that content into the header's left (title)
 * and right (actions) regions. This eliminates the old "two stacked
 * bars" pattern (a thin bell-only bar + each page's own title bar) — the
 * page title and the bell/user-menu now live in a single header.
 *
 * Why portals (not context state holding ReactNodes): the page's action
 * buttons keep their handlers and closures inside the page's own render
 * tree, so they're always fresh — no effect-based re-registration, no
 * stale config. The context only carries the two DOM mount points.
 */
type PageHeaderSlots = {
  titleEl: HTMLElement | null;
  actionsEl: HTMLElement | null;
  accentEl: HTMLElement | null;
  subheaderEl: HTMLElement | null;
  registerTitleEl: (el: HTMLElement | null) => void;
  registerActionsEl: (el: HTMLElement | null) => void;
  registerAccentEl: (el: HTMLElement | null) => void;
  registerSubheaderEl: (el: HTMLElement | null) => void;
};

const PageHeaderSlotsContext = createContext<PageHeaderSlots | null>(null);

export function PageHeaderSlotsProvider({ children }: { children: ReactNode }) {
  const [titleEl, setTitleEl] = useState<HTMLElement | null>(null);
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  const [accentEl, setAccentEl] = useState<HTMLElement | null>(null);
  const [subheaderEl, setSubheaderEl] = useState<HTMLElement | null>(null);

  return (
    <PageHeaderSlotsContext.Provider
      value={{
        titleEl,
        actionsEl,
        accentEl,
        subheaderEl,
        registerTitleEl: setTitleEl,
        registerActionsEl: setActionsEl,
        registerAccentEl: setAccentEl,
        registerSubheaderEl: setSubheaderEl,
      }}
    >
      {children}
    </PageHeaderSlotsContext.Provider>
  );
}

/**
 * Read the header mount points. Throws if used outside the provider so
 * a missing provider fails loudly in dev rather than silently dropping
 * the page title.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePageHeaderSlots(): PageHeaderSlots {
  const ctx = useContext(PageHeaderSlotsContext);
  if (!ctx) {
    throw new Error('usePageHeaderSlots must be used within <PageHeaderSlotsProvider>');
  }
  return ctx;
}
